import React from 'react';
import styled, { keyframes } from 'styled-components';

// --- TYPE DEFINITIONS ---
interface Summary {
    value: string;
    category: string;
    interpretation: string;
}

interface VitalSignCardProps {
    title: string;
    summary: Summary | null;
    icon: React.ReactNode;
    confidenceLevel?: number;
}

// --- STYLED COMPONENTS & HELPERS ---

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const clientDetails = localStorage.getItem('clientDetails');
const clientData = clientDetails ? JSON.parse(clientDetails) : null;

const getDynamicColors = (
    category: string = ''
): { main: string; background: string } => {
    const lowerCaseCat = category.toLowerCase();

    if (lowerCaseCat.includes('normal') || lowerCaseCat.includes('good')) {
        return { main: '#10B981', background: '#D1FAE5' }; // Green
    }
    if (
        lowerCaseCat.includes('fair') ||
        lowerCaseCat.includes('monitor') ||
        lowerCaseCat.includes('medium') ||
        lowerCaseCat.includes('balanced') ||
        lowerCaseCat.includes('mild')
    ) {
        return { main: '#F59E0B', background: '#FEF3C7' }; // Amber/Yellow
    }
    if (
        lowerCaseCat.includes('needs attention') ||
        lowerCaseCat.includes('low') ||
        lowerCaseCat.includes('warning') ||
        lowerCaseCat.includes('elevated') ||
        lowerCaseCat.includes('red') ||
        lowerCaseCat.includes('very high') ||
        lowerCaseCat.includes('high')
    ) {
        return { main: '#EF4444', background: '#FEE2E2' }; // Red
    }

    if (lowerCaseCat.includes('relaxed')) {
        return { main: '#8B5CF6', background: '#EDE9FE' }; // Purple
    }


    return { main: '#3B82F6', background: '#DBEAFE' }; // Default Blue
};

const CardWrapper = styled.div`
  background-color: #ffffff;
  border-radius: 16px;
  padding: 20px 24px;
  box-shadow: 0 4px 6px -1px rgba(99, 99, 99, 0.1), 0 2px 4px -2px rgba(99, 99, 99, 0.1);
  border: 1px solid #E5E7EB;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  animation: ${slideInUp} 0.6s ease-out forwards;
  opacity: 0;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 15px -3px rgba(99, 99, 99, 0.1), 0 4px 6px -4px rgba(99, 99, 99, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const IconContainer = styled.div<{ category: string }>`
  color: ${props => getDynamicColors(props.category).main};
  background-color: ${props => getDynamicColors(props.category).background};
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1F2937;
`;

const Value = styled.p`
  font-size: 2rem;
  font-weight: 700;
  color: #1F2937;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: 0 0 8px 0;
`;

const CategoryBadge = styled.span<{ category: string }>`
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 9999px;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: ${props => getDynamicColors(props.category).main};
`;

const Interpretation = styled.p`
  color: #6B7280;
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
`;

const UnitText = styled.span`
    font-size: 1.25rem; /* Smaller than the main value */
    font-weight: 500;
    color: #6B7280; /* Lighter color */
    margin-left: 4px;
`;


// Styled component for confidence badge
const ConfidenceBadge = styled.div<{ level: number }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  margin-top: 0.5rem;

  color: ${({ level }) =>
        level === 1 ? "#000" :
            level === 2 ? "#000" :
                "#000"};              

  &::before {
    content: "";
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${({ level }) =>
        level === 1 ? "#16a34a" :
            level === 2 ? "#f59e0b" :
                "#dc2626"};             
  }
`;

// Helper to map level → label
const getConfidenceLabel = (level: number) => {
    switch (level) {
        case 1: return "High Confidence";
        case 2: return "Medium Confidence";
        case 3: return "Low Confidence";
        default: return "";
    }
};

const VitalSignCard: React.FC<VitalSignCardProps> = ({ title, summary, icon, confidenceLevel }) => {
    if (!summary) {
        return null;
    }

    const { value, category, interpretation } = summary;

    // This regex looks for a number/slash part and a text part.
    const valueParts = value.match(/^([\d./\s]+)\s*([a-zA-Z/]+)/);

    const renderValue = () => {
        if (valueParts) {
            const number = valueParts[1].trim();
            const unit = valueParts[2].trim();
            return (
                <span>
                    {number}
                    <UnitText>{unit}</UnitText>
                </span>
            );
        }
        return <span>{value}</span>;
    };

    return (
        <CardWrapper>
            <CardHeader>
                <IconContainer category={category}>{icon}</IconContainer>
                <Title>{title}</Title>
            </CardHeader>
            <div>
                <Value>
                    {renderValue()}
                    <CategoryBadge category={category}>
                        {category}
                    </CategoryBadge>
                </Value>
                <Interpretation>{interpretation}</Interpretation>
                {confidenceLevel && clientData && clientData.show_confidence_level == 1 && (
                    <ConfidenceBadge level={confidenceLevel}>
                        {getConfidenceLabel(confidenceLevel)}
                    </ConfidenceBadge>
                )}
            </div>
        </CardWrapper>
    );
};

export default VitalSignCard;